import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslations } from '../../contexts/Translations';
import { useUser } from '../../contexts/User';
import { ValidationError } from 'ts-core';
import { GoogleOAuthButton } from '../GoogleOAuthButton';
import type { AuthView } from './ForgotPasswordForm';

interface RegisterFormProps {
  setView: (view: AuthView) => void;
}

interface RegisterFormData {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export default function RegisterForm({
  setView,
}: RegisterFormProps) {
  const [serverError, setServerError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState<string>('');
  const { translated, language } = useTranslations();
  const { register: userRegister, loginWithOAuth } = useUser();

  const validationSchema = yup.object().shape({
    username: yup
      .string()
      .required(translated('username_required'))
      .max(255, translated('username_too_long')),
    email: yup
      .string()
      .email(translated('email_invalid'))
      .required(translated('email_required')),
    password: yup
      .string()
      .required(translated('password_required'))
      .min(6, translated('password_too_short')),
    confirmPassword: yup
      .string()
      .required(translated('password_confirm_required'))
      .oneOf([yup.ref('password')], translated('passwords_must_match')),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<RegisterFormData>({
    resolver: yupResolver(validationSchema),
  });

  const fields: (keyof RegisterFormData)[] = [
    'username',
    'email',
    'password',
    'confirmPassword',
  ];

  const handleRegisterSubmit = async (data: RegisterFormData) => {
    try {
      setIsLoading(true);
      setServerError(null);
      await userRegister!(data.email, data.password, data.username, language);
      // Registration successful - show verification message
      setRegisteredEmail(data.email);
      setRegistrationSuccess(true);
    } catch (e: any) {
      if (e instanceof ValidationError) {
        setServerError(e.message);
        // Map backend field errors to form fields
        for (const errorField of Object.keys(e.errors)) {
          if (fields.includes(errorField as keyof RegisterFormData)) {
            setError(errorField as keyof RegisterFormData, {
              message: e.errors[errorField].join(', '),
            });
          }
        }
      } else {
        setServerError(e.message || translated('registration_error'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setOauthLoading(true);
    setServerError(null);
    try {
      await loginWithOAuth!('google');
    } catch (error: any) {
      setServerError(error.message || 'Google login failed');
    } finally {
      setOauthLoading(false);
    }
  };

  // Show success message after registration
  if (registrationSuccess) {
    return (
      <div className="mt-2 text-center">
        <div className="text-green-500 text-5xl mb-4">&#9993;</div>
        <h3 className="text-lg font-semibold mb-2">
          {translated('registration_success_title') || 'Check your email!'}
        </h3>
        <p className="text-gray-600 mb-4">
          {translated('verification_email_sent') ||
            'We have sent a verification email to:'}
        </p>
        <p className="font-medium text-gray-900 mb-4">{registeredEmail}</p>
        <p className="text-sm text-gray-500 mb-4">
          {translated('verification_email_instructions') ||
            'Please click the link in the email to verify your account. Check your spam folder if you don\'t see it.'}
        </p>
        <button
          type="button"
          className="mt-2 text-balloon inline-flex justify-center rounded-full border border-transparent px-4 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          onClick={() => setView('login')}
        >
          {translated('back_to_login') || 'Back to Login'}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-2">
      {/* Google OAuth Button */}
      <GoogleOAuthButton
        onSuccess={handleGoogleLogin}
        onError={(error) => setServerError(error.message)}
        loading={oauthLoading}
        text="Continue with Google"
        className="mb-4"
      />

      {/* Divider */}
      <div className="flex items-center my-4">
        <div className="flex-1 border-t border-gray-300"></div>
        <span className="px-4 text-sm text-gray-500">
          {translated('or') || 'or'}
        </span>
        <div className="flex-1 border-t border-gray-300"></div>
      </div>

      <form
        onSubmit={handleSubmit(handleRegisterSubmit)}
        className="flex flex-col items-end"
      >
        <input
          type="text"
          {...register('username')}
          placeholder={translated('username')}
          className="w-full border-2 border-gray-300 p-2 rounded-lg mt-2"
          disabled={isLoading}
          aria-invalid={!!errors.username}
          aria-describedby={errors.username ? 'username-error' : undefined}
        />
        {errors.username && (
          <p
            id="username-error"
            className="text-red-400 text-sm w-full text-left mb-2"
            role="alert"
          >
            {errors.username.message}
          </p>
        )}

        <input
          type="email"
          placeholder={translated('email')}
          {...register('email')}
          className="w-full border-2 border-gray-300 p-2 rounded-lg mt-2"
          disabled={isLoading}
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? 'email-error' : undefined}
        />
        {errors.email && (
          <p
            id="email-error"
            className="text-red-400 text-sm w-full text-left mb-2"
            role="alert"
          >
            {errors.email.message}
          </p>
        )}

        <input
          type="password"
          placeholder={translated('password')}
          {...register('password')}
          className="w-full border-2 border-gray-300 p-2 rounded-lg mt-2"
          disabled={isLoading}
          aria-invalid={!!errors.password}
          aria-describedby={errors.password ? 'password-error' : undefined}
        />
        {errors.password && (
          <p
            id="password-error"
            className="text-red-400 text-sm w-full text-left mb-2"
            role="alert"
          >
            {errors.password.message}
          </p>
        )}

        <input
          type="password"
          placeholder={translated('password_confirm')}
          {...register('confirmPassword')}
          className="w-full border-2 border-gray-300 p-2 rounded-lg mt-2"
          disabled={isLoading}
          aria-invalid={!!errors.confirmPassword}
          aria-describedby={
            errors.confirmPassword ? 'confirm-password-error' : undefined
          }
        />
        {errors.confirmPassword && (
          <p
            id="confirm-password-error"
            className="text-red-400 text-sm w-full text-left mb-2"
            role="alert"
          >
            {errors.confirmPassword.message}
          </p>
        )}

        <span className="mt-2 text-sm w-full text-left">
          {translated('accept_terms')}{' '}
          <a
            href="/agb"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            {translated('terms')}
          </a>
        </span>

        {serverError && (
          <p
            className="text-red-400 text-sm w-full text-left mt-2 mb-2"
            role="alert"
          >
            {serverError}
          </p>
        )}

        <input
          className="bg-balloon text-white inline-flex mt-2 justify-center rounded-full border border-transparent px-4 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          type="submit"
          value={
            isLoading
              ? translated('loading') || 'Loading...'
              : translated('register')
          }
          disabled={isLoading}
        />

        <button
          type="button"
          className="mt-2 text-balloon inline-flex justify-center rounded-full border border-transparent px-4 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          onClick={() => setView('login')}
          disabled={isLoading}
        >
          {translated('already_registered')}
        </button>
      </form>
    </div>
  );
}
